import {
    ArrowBack,
    CameraAlt,
    Comment,
    Favorite,
    FavoriteBorder,
    OndemandVideoOutlined,
    Photo,
    Report,
    SearchRounded,
} from '@mui/icons-material';
import {
    AppBar,
    Avatar,
    Box,
    Button,
    Card,
    CardActionArea,
    CardActions,
    CardContent,
    Grid,
    IconButton,
    InputBase,
    Toolbar,
    Typography,
    useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToggleLikeMutation } from '../api/feedSlice';
import ListEmpty from '../components/fragments/ListEmpty';
import Screen from '../components/fragments/Screen';
import useSettings from '../hooks/useSettings';
import { rtGetListData } from '../store/slices/listSlice';

export default function FeedTab() {
    const [search, setSearch] = useState('');

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const state = useSelector((st) => st);

    const { user } = state.auth;
    const { feed, busy } = state.list2;

    useEffect(() => {
        dispatch(rtGetListData('posts', 'feed'));
    }, [dispatch]);

    return (
        <Screen
            tab
            loading={busy['feed']}
            disablePadding
            disableAppBar
            disableSpacing
        >
            <FeedAppBar onSearch={(text) => setSearch(text)} />
            <Box sx={{ width: '100%', height: 72 }} />
            <Grid item xs={12} sx={{}}>
                <Card variant="outlined" sx={{ mx: 1.5, pb: 1, pt: 2, my: 0 }}>
                    <CardContent
                        sx={{ mx: 0, py: 0, px: 1.5 }}
                        className="d-flex align-items-center"
                    >
                        <Avatar sx={{ mr: 1 }} src={user?.links?.avatar}>
                            {user?.firstName?.slice(0, 2)}
                        </Avatar>
                        <CardActionArea
                            onClick={() => navigate('/create-post')}
                        >
                            <div
                                className="w-100 d-flex align-items-center"
                                style={{
                                    height: 36,
                                    borderRadius: theme.shape.borderRadius,
                                    background:
                                        theme.palette.mode === 'light'
                                            ? '#ddd'
                                            : theme.palette.background.default,
                                }}
                            >
                                <Typography
                                    sx={{ mx: 1 }}
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    What&apos;s on your mind?
                                </Typography>
                            </div>
                        </CardActionArea>
                    </CardContent>
                    <CardContent
                        sx={{ my: 0, py: 0 }}
                        className="d-flex align-items-center pb-0"
                    >
                        <Avatar
                            sx={{ mr: 1 }}
                            style={{ visibility: 'hidden', height: 10 }}
                        />
                        <Button
                            sx={{ mr: 1 }}
                            className="text-capitalize"
                            startIcon={<CameraAlt />}
                            size="medium"
                            onClick={() => {
                                navigate('/create-post', {
                                    state: { alt: 'camera' },
                                });
                            }}
                        >
                            Camera
                        </Button>
                        <Button
                            className="text-capitalize"
                            startIcon={<Photo />}
                            size="medium"
                            onClick={() => {
                                navigate('/create-post', {
                                    state: { alt: 'photo' },
                                });
                            }}
                        >
                            Photo
                        </Button>
                    </CardContent>
                </Card>
            </Grid>

            {feed
                ?.filter((feedItem) =>
                    JSON.stringify(feedItem)
                        .toLocaleLowerCase()
                        .includes(search.toLowerCase())
                )
                ?.map((feedItem) => (
                    <FeedCard key={feedItem?.id} feed={feedItem} />
                ))}

            {!busy['feed'] && feed?.length < 1 && <ListEmpty />}
        </Screen>
    );
}

const FeedCard = ({ feed }) => {
    const { dates, id, interactions, media, member, post } = feed;

    const [liked, setLiked] = useState(interactions?.liked);
    const [likes, setLikes] = useState(interactions?.likes);

    const navigate = useNavigate();
    const classes = useStyles();
    const { layout } = useSettings();

    const spaced = layout === 'spaced';

    const [toggleLike] = useToggleLikeMutation();

    const handleNavigate = () => {
        navigate(`/feed/${id}`, { state: feed });
    };

    const handleLike = () => {
        toggleLike({ id, type: 'posts' });
        liked ? setLikes(likes - 1) : setLikes(likes + 1);
        setLiked(!liked);
    };

    const handleReport = () => {
        navigate(`/report/${id}`);
    };

    return (
        <Grid item xs={12} className={classes.grid}>
            <div className={`${spaced && 'd-flex'} px-2 py-3`}>
                {!!spaced && (
                    <Avatar src={member?.avatar} aria-label="profile">
                        {member?.name?.slice(0, 2)}
                    </Avatar>
                )}
                <div style={{ flex: 1 }}>
                    <CardContent className="py-0" onClick={handleNavigate}>
                        <div className={`${!spaced && 'd-flex'}`}>
                            {!spaced && (
                                <Avatar
                                    src={member?.avatar}
                                    aria-label="profile"
                                >
                                    {member?.name?.slice(0, 2)}
                                </Avatar>
                            )}
                            <div className={`${!spaced && 'ms-3'}`}>
                                <Typography className="fw-bold">
                                    {member?.name}
                                </Typography>
                                <Typography
                                    color="InactiveCaptionText"
                                    className="mb-2"
                                    variant="body2"
                                >
                                    {moment(dates?.updated).fromNow()}
                                </Typography>
                            </div>
                        </div>
                        <Typography style={{ marginBottom: 10 }}>
                            {post}
                        </Typography>

                        <FeedMedia media={media} feed={feed} />
                    </CardContent>
                    <CardActions
                        sx={{ mx: 2 }}
                        className="justify-content-between"
                    >
                        <Button
                            color="secondary"
                            size="medium"
                            startIcon={
                                liked ? <Favorite /> : <FavoriteBorder />
                            }
                            onClick={handleLike}
                        >
                            {likes || '0'}
                        </Button>
                        <Button
                            size="medium"
                            startIcon={<Comment />}
                            onClick={handleNavigate}
                        >
                            {interactions?.comments || '0'}
                        </Button>
                        <Button
                            className="text-capitalize"
                            size="medium"
                            startIcon={<Report />}
                            onClick={handleReport}
                        ></Button>
                    </CardActions>
                </div>
            </div>
        </Grid>
    );
};

export const FeedMedia = ({ media, feed }) => {
    const theme = useTheme();

    return (
        <>
            {media
                .filter(({ type }) => type === 'image')
                .map(({ link }) => {
                    const length = media?.filter(
                        ({ type }) => type === 'image'
                    )?.length;

                    if (length !== 1) {
                        return null;
                    }

                    return (
                        <div key={link}>
                            <img
                                src={link}
                                alt=""
                                style={{
                                    width: '100%',
                                    borderRadius: theme.shape.borderRadius,
                                    border:
                                        theme.palette.mode == 'light' &&
                                        `2px solid #ddd`,
                                    background:
                                        theme.palette.mode === 'light'
                                            ? '#ddd'
                                            : theme.palette.background.paper,
                                }}
                            />
                        </div>
                    );
                })}

            <div className="d-flex">
                {media
                    .filter(({ type }) => type === 'image')
                    .map(({ link }, index) => {
                        const length = media?.filter(
                            ({ type }) => type === 'image'
                        )?.length;

                        if (index > 1) {
                            return null;
                        }

                        if (length == 1) {
                            return null;
                        }

                        return (
                            <FeedImage
                                key={link}
                                index={index}
                                link={link}
                                length={length}
                                feed={feed}
                            />
                        );
                    })}
            </div>

            <div className="d-flex">
                {media
                    .filter(({ type }) => type === 'image')
                    .map(({ link }, index) => {
                        const length = media?.filter(
                            ({ type }) => type === 'image'
                        )?.length;

                        if (index < 2) {
                            return null;
                        }

                        if (length == 1) {
                            return null;
                        }

                        return (
                            <FeedImage
                                key={link}
                                index={index}
                                link={link}
                                length={length}
                                feed={feed}
                            />
                        );
                    })}
            </div>
            {media
                .filter(({ type }) => type === 'video')
                .map(({ link }) => (
                    <video
                        key={link}
                        className="mt-2"
                        controls="controls"
                        preload="none"
                        width="100%"
                    >
                        <source id="mp4" src={link} type="video/mp4" />
                        <Typography color="primary">
                            Your user agent does not support the HTML5 Video
                            element.
                        </Typography>
                    </video>
                ))}
        </>
    );
};

const FeedImage = ({ link, index, length, feed, onImageClick }) => {
    const theme = useTheme();
    const navigate = useNavigate();

    let width = '100%';
    let height = 200;
    let marginRight = 0;
    let marginLeft = 0;

    if (length > 1 && index < 2) {
        width = '48%';
    }

    if (length > 1 && index === 0) {
        marginRight = '1%';
    }

    if (length > 1 && index === 1) {
        marginLeft = '1%';
    }

    if (length > 2) {
        height = 140;
    }

    return (
        <div
            className="mt-2"
            style={{
                height,
                width,
                borderRadius: theme.shape.borderRadius,
                backgroundColor: 'rgba(0,0,0,.2)',
                backgroundBlendMode: 'soft-light',
                backgroundImage: `url("${link}")`,
                backgroundSize: 'cover',
                backgroundPositionX: 'center',
                marginRight,
                marginLeft,
            }}
            onClick={() => {
                feed && navigate(`/feed/${feed?.id}`, { state: feed });

                onImageClick && onImageClick(feed);
            }}
        />
    );
};

const FeedAppBar = ({ onSearch }) => {
    const [searchOpen, setSearchOpen] = useState(false);

    const navigate = useNavigate();
    const classes = useStyles();

    return (
        <AppBar className={classes.appbar} position="fixed" elevation={4}>
            <Toolbar className={classes.toolbar}>
                {searchOpen && (
                    <>
                        <IconButton
                            edge="start"
                            className={classes.menuButton}
                            aria-label="menu"
                            onClick={() => setSearchOpen(false)}
                        >
                            <ArrowBack />
                        </IconButton>
                        <InputBase
                            placeholder="Search…"
                            classes={{
                                root: classes.inputRoot,
                                input: classes.inputInput,
                            }}
                            inputProps={{ 'aria-label': 'search' }}
                            onChange={(e) => onSearch(e.target.value)}
                        />
                    </>
                )}
                {!searchOpen && (
                    <Typography
                        variant="h5"
                        noWrap={true}
                        className={classes.title}
                    >
                        Feed
                    </Typography>
                )}
                {!searchOpen && (
                    <>
                        <IconButton
                            color="primary"
                            aria-label="menu"
                            onClick={() => setSearchOpen(true)}
                        >
                            <SearchRounded />
                        </IconButton>
                        <IconButton
                            sx={{ ml: 1 }}
                            color="primary"
                            aria-label="menu"
                            onClick={() => navigate('/guest-feed')}
                        >
                            <OndemandVideoOutlined color="secondary" />
                        </IconButton>
                    </>
                )}
            </Toolbar>
        </AppBar>
    );
};

const useStyles = makeStyles((theme) => ({
    grid: {
        borderBottomWidth: 2,
        borderBottomColor: theme.palette.divider,
        borderBottomStyle: 'solid',
    },
    menuButton: {
        marginRight: theme.spacing(2),
    },
    title: {
        flexGrow: 1,
        fontWeight: 'bold',
        color:
            theme.palette.mode === 'light'
                ? theme.palette.primary.main
                : '#fff',
    },
    toolbar: {
        backgroundColor: theme.palette.background.default,
        boxShadow: 'none',
    },
    appbar: {
        margin: 'auto',
        backgroundColor: theme.palette.mode === 'light' && '#fff',
    },
}));
